// ─── Strategy Domain Types ───
// Moved from src/mocks/strategies.ts so we can use real data without mock imports

export interface Indicator {
  id: string;
  type: 'SMA' | 'BOLLINGER' | 'HMA' | 'TMA';
  period: number;
  deviation?: number;
}

export interface TrailingStopParams {
  mode: 'setupOnly' | 'simple' | 'full';
  profitReach: number;
  step: number;
  takeProfit: number;
  stopLoss: number;
  usePriceSLTP: boolean;
  takeProfitPrice: number | null;
  stopLossPrice: number | null;
}

export interface ChinBotParams {
  symbols: string[];
}

export const DEFAULT_CHIN_BOT_PARAMS: ChinBotParams = {
  symbols: [
    'EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'NZDUSD',
    'XAUUSD', 'GBPJPY', 'EURJPY', 'EURGBP', 'USDCHF',
  ],
};

export interface EntryRule {
  id: string;
  indicatorId: string;
  condition: 'crosses_above' | 'crosses_below' | 'greater_than' | 'less_than' | 'equal_to';
  targetType: 'price' | 'level' | 'indicator';
  targetValue: string;
  logic?: 'AND' | 'OR';
  side?: 'buy' | 'sell';
}

export interface ExitRule {
  id: string;
  indicatorId: string;
  condition: 'crosses_above' | 'crosses_below' | 'greater_than' | 'less_than' | 'equal_to';
  targetType: 'price' | 'level' | 'indicator';
  targetValue: string;
  logic?: 'AND' | 'OR';
  side?: 'buy' | 'sell';
}

export interface AssignedAccount {
  id: string;
  platform: 'mt4' | 'ctrader';
  account_id: string;
  account_name: string;
  balance?: number;
  currency?: string;
  is_live?: boolean;
}

export interface Strategy {
  id: string;
  user_id: string;
  name: string;
  type: 'generic' | 'trailingStop' | 'chin' | 'autochinbot';
  symbol: string;
  symbol_list_id: string | null;
  timeframe: string;
  description: string;
  indicators: Indicator[];
  entry_rules: EntryRule[];
  exit_rules: ExitRule[];
  risk_per_trade: number;
  max_positions: number;
  stop_loss_pips: number;
  take_profit_pips: number;
  mt4_copy_enabled: boolean;
  ctrader_copy_enabled: boolean;
  assigned_accounts: AssignedAccount[];
  is_active: boolean;
  active_accounts: string[];
  trailingStopParams: TrailingStopParams | null;
  chinBotParams: ChinBotParams | null;
  created_at: string;
  updated_at: string;
}

export const INDICATOR_LABELS: Record<string, string> = {
  SMA: 'Simple Moving Average',
  BOLLINGER: 'Bollinger Bands',
  HMA: 'Hull Moving Average',
  TMA: 'Triangular Moving Average',
};

export const TIMEFRAMES = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1', 'MN'];

export const SYMBOLS = [
  'EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'NZDUSD',
  'XAUUSD', 'XAGUSD', 'BTCUSD', 'ETHUSD', 'GBPJPY', 'EURJPY',
  'EURGBP', 'USDCHF', 'US30', 'US100', 'GER40',
];

export const CONDITION_LABELS: Record<string, string> = {
  crosses_above: 'Crosses Above',
  crosses_below: 'Crosses Below',
  greater_than: 'Greater Than',
  less_than: 'Less Than',
  equal_to: 'Equal To',
};

export const TARGET_TYPE_LABELS: Record<string, string> = {
  price: 'Price',
  level: 'Fixed Level',
  indicator: 'Another Indicator',
};